import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { ViewTitle } from '../../components/ViewTitle';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../redux/actions';
import dayjs from 'dayjs';
import { getMessaging, getToken } from 'firebase/messaging';
import app from '../../configs/firebase';

const Profile = ({ title, subtitle }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const employee = useSelector((state) => state.employee);
  const messaging = getMessaging(app);
  const vapidKey = process.env.VITE_VAPID_KEY;

  useEffect(() => {
    dispatch(actions.meGet());
    dispatch(actions.employeeGet(me?.userData?._id));
  }, []);

  const registerNotify = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        alert("Notification permission denied");
        return;
      }
  
      const currentToken = await getToken(messaging, { vapidKey });
  
      if (!currentToken) {
        console.warn("No FCM token received");
        return;
      }
      const payload = {
        browserToken: currentToken || '',
      };
  
      await dispatch(actions.employeePut(me?.userData?._id, payload));
      alert("Token received, check console");
      dispatch(actions.employeeGet(me?.userData?._id));
    } catch (error) {
      console.error("Error getting FCM token:", error);
    }
  };

  const handleRemoveNotify = async () => {
    await dispatch(actions.employeePut(me?.userData?._id, { browserToken: null }));
    dispatch(actions.employeeGet(me?.userData?._id));
  };

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin h-8 w-8 border-2 border-blue-400 rounded-full border-t-transparent"></div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-red-500 p-4 bg-red-50 rounded-lg shadow-sm max-w-md mx-auto">
        {error}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex flex-col">
      <div className="w-full max-w-7xl">
        {renderTitle()}
      </div>

      <div className="flex-1 flex flex-col items-center pb-5">
        <div className="w-full max-w-7xl bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-10">

          {/* Profile Header */}
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="flex justify-center md:justify-start">
              {me?.userData?.image ? (
                <img
                  src={me?.userData?.image}
                  alt="Profile"
                  className="w-32 h-32 md:w-36 md:h-36 rounded-full object-cover shadow-md border-2 border-white"
                />
              ) : (
                <div className="w-32 h-32 md:w-36 md:h-36 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 text-5xl font-light shadow-md border-2 border-white">
                  {me?.userData?.firstname?.charAt(0)}
                </div>
              )}
            </div>

            <div className="flex flex-col items-center md:items-start">
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">
                {me?.userData?.firstname} {me?.userData?.lastname}
              </h1>
              <p className="text-gray-500 mt-2">
                {me?.userData?.role?.name} • {me?.userData?.department?.name}
              </p>
            </div>
          </div>

          {/* Information Container */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            
            {/* Personal Information */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
                ข้อมูลส่วนตัว
              </h2>
              <div className="space-y-6">
                {[
                  { label: 'ชื่อ-นามสกุล', value: `${me?.userData?.firstname || ''} ${me?.userData?.lastname || ''}` },
                  { label: 'เบอร์โทร', value: me?.userData?.tel || "-" },
                  { label: 'E-mail', value: me?.userData?.email || "-" },
                  { label: 'ที่อยู่', value: me?.userData?.address || "-" }
                ].map((item, index) => (
                  <div key={index} className="flex flex-col">
                    <div className="text-gray-500 text-sm mb-1">{item.label}</div>
                    <div className="font-medium">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Work Information */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
                ข้อมูลการทำงาน
              </h2>
              <div className="space-y-6">
                {[
                  { label: 'รหัสแผนก', value: me?.userData?.department?.department_code || "-" },
                  { label: 'วันที่เริ่มงาน', value: me?.userData?.uid?.createdAt ? dayjs(me?.userData?.uid?.createdAt).format('DD MMMM YYYY') : "-" },
                  { label: 'แผนก', value: me?.userData?.department?.name || "-" },
                  { label: 'ตำแหน่ง', value: me?.userData?.role?.name || "-" }
                ].map((item, index) => (
                  <div key={index} className="flex flex-col">
                    <div className="text-gray-500 text-sm mb-1">{item.label}</div>
                    <div className="font-medium">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Browser Notification Section */}
          <div className="mt-12 pt-6 border-t border-gray-100">
            <div className="bg-blue-50 rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">การแจ้งเตือนผ่าน Browser</h3>
                <p className="text-gray-600 text-sm">เปิดใช้งานการแจ้งเตือนเพื่อรับข่าวสารและการอัพเดทล่าสุด</p>
              </div>

              {employee?.browserToken == null ? (
                <Button 
                  onClick={registerNotify} 
                  variant="contained" 
                  color="primary" 
                  size="medium"
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500, padding: '8px 20px' }}
                >
                  รับการแจ้งเตือน
                </Button>
              ) : (
                <div className="flex flex-col items-start md:items-end gap-2">
                  <div className="flex items-center text-green-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>คุณได้เปิดใช้งานการแจ้งเตือนแล้ว</span>
                  </div>
                  <Button 
                    onClick={handleRemoveNotify} 
                    variant="outlined" 
                    color="error" 
                    size="small"
                    sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', padding: '4px 12px' }}
                  >
                    ยกเลิกรับการแจ้งเตือน
                  </Button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

Profile.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default Profile;
